import React, { useState, useEffect } from 'react';
import { useTranslation } from '@hooks/useTranslation';
import { useCommonForm } from './hooks/useCommonForm';
import ErrorDisplay from './components/ErrorDisplay';
import IntegerInput from './components/IntegerInput';
import api from '@services/api';
import './ConsumptionForm.scss';

const ConsumptionForm = ({
    data,
    consumption,
    onSubmit,
    onCancel,
    isEditing = false,
    loading: externalLoading = false,
    resource = null
}) => {
    const { t } = useTranslation();

    // Support both `data` (from common components) and `consumption` (legacy)
    const modelData = data || consumption;

    // Initialize form with validation rules
    const initialFormData = {
        quantity_consumed: modelData?.quantity_consumed || 1,
        notes: modelData?.notes || ''
    };

    const validationRules = {
        quantity_consumed: {
            required: true,
            custom: (value, formData) => {
                const numValue = parseInt(value) || 0;
                if (numValue <= 0) {
                    return t('consumption.invalid_quantity') || 'Quantity must be greater than 0';
                }
                if (!isEditing && resource && numValue > (resource.current_quantity ?? resource.quantity ?? 0)) {
                    return t('consumption.insufficient_quantity') || 'Insufficient quantity available';
                }
                return null;
            }
        }
    };

    const {
        formData,
        loading,
        error,
        fieldErrors,
        handleSubmit,
        updateFormData,
        getFieldProps,
        handleError
    } = useCommonForm({
        initialData: initialFormData,
        onSubmit: async (submitData) => {
            const cleanedData = {
                quantity_consumed: submitData.quantity_consumed,
                notes: submitData.notes || null
            };

            await onSubmit(cleanedData);
        },
        externalLoading,
        validationRules
    });

    // Update form data when modelData changes
    useEffect(() => {
        if (modelData) {
            const newFormData = {
                quantity_consumed: modelData.quantity_consumed || 1,
                notes: modelData.notes || ''
            };
            updateFormData(newFormData);
        } else {
            updateFormData({
                quantity_consumed: 1,
                notes: ''
            });
        }
    }, [modelData]); // Removed updateFormData dependency

    // Custom handler for quantity field to ensure it's always a number
    const handleQuantityChange = (e) => {
        const value = parseInt(e.target.value) || 0;
        const syntheticEvent = {
            target: {
                name: 'quantity_consumed',
                value: value,
                type: 'number'
            }
        };
        // Use the form's handleChange but with processed value
        getFieldProps('quantity_consumed').onChange(syntheticEvent);
    };

    // Get unit from resource metadata or fallback to default
    const unit = resource?.resource_metadata?.find(meta => meta.metadata_key === 'unit')?.value || t('consumption.unit_pieces', 'pcs');

    return (
        <form onSubmit={handleSubmit} className="consumption-form">
            <ErrorDisplay error={error} fieldErrors={fieldErrors} />

            {resource && (
                <div className="resource-info">
                    <h4>{t('consumption.resource')}: {resource.name}</h4>
                    <p>{resource.description}</p>
                    <p className="quantity-info">
                        {t('consumption.available_quantity')}: {resource.current_quantity ?? resource.quantity ?? 0}
                    </p>
                </div>
            )}

            <div className="form-group">
                <label htmlFor="quantity_consumed">
                    {t('consumption.quantity_to_consume')} *
                </label>
                <IntegerInput
                    id="quantity_consumed"
                    name="quantity_consumed"
                    value={formData.quantity_consumed}
                    onChange={handleQuantityChange}
                    disabled={loading}
                    min={1}
                    max={resource?.current_quantity ?? resource?.quantity ?? 999}
                    className={fieldErrors.quantity_consumed ? 'error' : ''}
                    error={!!fieldErrors.quantity_consumed}
                    required
                    unit={unit}
                />
            </div>

            <div className="form-group">
                <label htmlFor="notes">
                    {t('consumption.notes')}
                </label>
                <textarea
                    id="notes"
                    {...getFieldProps('notes', {
                        placeholder: t('consumption.notes_placeholder'),
                        rows: 3,
                        className: 'form-control'
                    })}
                />
            </div>

            <div className="form-actions">
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="btn btn-secondary"
                        disabled={loading}
                    >
                        {t('common.cancel')}
                    </button>
                )}
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                >
                    {loading ? t('common.loading') : (isEditing ? t('common.save') : t('consumption.consume'))}
                </button>
            </div>
        </form>
    );
};

export default ConsumptionForm;
